import Vue from 'vue';
import Meta from 'vue-meta';
import { sync } from 'vuex-router-sync';
import NoSSR from 'vue-no-ssr';
import VueKit from '@homeservices/vue-kit';
import setupPlatform from '@homeservices/api-kit/setup';
import http from '@homeservices/api-kit/axios';
import Vuex from 'vuex';
import VueRouter from 'vue-router';
import * as VueGoogleMaps from 'vue2-google-maps';

import {
  calendarDate,
  dateDuration,
  timeOfDay,
  timeReceivedDefault,
  timeReceivedJobs,
  timer,
} from '@homeservices/tool-kit/proreferral/utils/time-formats';
import PortalVue from 'portal-vue';
import interceptorSetup from '@/utils/interceptors';
import App from '@/App';


import { Api } from '@/plugins/api';
import { Events } from '@/plugins/events';
import Device, { createDevice } from '@/plugins/device';
import RuntimeConstants from '@/plugins/runtime-constants';
import FireToast from '@/plugins/fire-toast';

import { createStore } from '@/store';
import { createRouter } from '@/router';
import { createEvents } from '@/events';

import Avatar from '@/components/Avatar';
import ProAvatar from '@/components/ProAvatar';
import Gallery from '@/components/Gallery';
import LoadingIndicator from '@/components/LoadingIndicator';
import Spinner from '@/components/Spinner';
import ImageUpload from '@/components/ImageUpload';
import RatingStars from '@/components/RatingStars';
import Thumbnail from '@/components/Thumbnail';

import { background } from '@/directives/background';
import autoExpand from '@/directives/auto-expand';
import infiniteScroll from '@/directives/infinite-scroll';
import newLabel from '@/directives/new-label';

import { lowercase } from '@/filters/lowercase';
import capitalize from '@/filters/capitalize';
import capitalizeAll from '@/filters/capitalizeAll';
import formatDate from '@/filters/format-date';
import formatNumber from '@/filters/format-number';
import phoneNumber from '@/filters/phone-number';
import location from '@/filters/location';
import fullName from '@/filters/full-name';
import pluralize from '@/filters/pluralize';
import toFixed from '@/filters/to-fixed';
import truncateText from '@/filters/truncate-text';
import counter from '@/filters/counter';
import trimEmail from '@/filters/trim-email';


import { errorSetup } from '@/error';
import { prefetchSetup } from '@/prefetch';

import { userIdentify } from '@/tracking/user-identify';


Vue.use(PortalVue);

Vue.use(Vuex);
Vue.use(VueKit);
Vue.use(VueRouter);
Vue.use(Events);
Vue.use(Device);
Vue.use(Api);
Vue.use(Meta);
Vue.use(FireToast);

Vue.component('Avatar', Avatar);
Vue.component('ProAvatar', ProAvatar);
Vue.component('Gallery', Gallery);
Vue.component('LoadingIndicator', LoadingIndicator);
Vue.component('Spinner', Spinner);
Vue.component('ImageUpload', ImageUpload);
Vue.component('RatingStars', RatingStars);
Vue.component('ClientOnly', NoSSR);
Vue.component('Thumbnail', Thumbnail);

Vue.directive('background', background);
Vue.directive('auto-expand', autoExpand);
Vue.directive('infinite-scroll', infiniteScroll);
Vue.directive('new-label', newLabel);

Vue.filter('lowercase', lowercase);
Vue.filter('capitalize', capitalize);
Vue.filter('capitalizeAll', capitalizeAll);
Vue.filter('pluralize', pluralize);
Vue.filter('truncateText', truncateText);
Vue.filter('toFixed', toFixed);
Vue.filter('calendarDate', calendarDate);
Vue.filter('timeOfDay', timeOfDay);
Vue.filter('timer', timer);
Vue.filter('dateDuration', dateDuration);
Vue.filter('timeReceivedDefault', timeReceivedDefault);
Vue.filter('timeReceivedJobs', timeReceivedJobs);
Vue.filter('formatDate', formatDate);
Vue.filter('formatNumber', formatNumber);
Vue.filter('phoneNumber', phoneNumber);
Vue.filter('location', location);
Vue.filter('fullName', fullName);
Vue.filter('counter', counter);
Vue.filter('trimEmail', trimEmail);

Vue.config.productionTip = false;

// Expose a factory function that creates a fresh
// app instance on each call (which is called for each SSR request)
export function createApp (context) {
  Vue.use(RuntimeConstants, {
    SALESFORCE_BECOME_A_PRO: `${context.globals.SALESFORCE_ACCOUNT_REDIRECT_DOMAIN}/pro/becomeapro`,
    SALESFORCE_ACCOUNT_REDIRECT_DOMAIN: context.globals.SALESFORCE_ACCOUNT_REDIRECT_DOMAIN,
    SALESFORCE_HELP_CENTER: context.globals.SALESFORCE_HELP_CENTER,
    RECAPTCHA_KEY: context.globals.RECAPTCHA_KEY,
    HOSTNAME: context.globals.HOSTNAME,
    TMX_ORG_ID: context.globals.TMX_ORG_ID,
  });

  function waitForMaps () {
    if (typeof window.$mapsKey !== 'undefined') {
      Vue.use(VueGoogleMaps, {
        load: {
          key: window.$mapsKey,
          libraries: 'places,drawing',
        },
      });
    } else {
      setTimeout(waitForMaps, 250);
    }
  }

  waitForMaps();


  const { skeletonKeyStore } = setupPlatform({
    clientName: context.globals.SKELETON_KEY_CLIENT_NAME,
    path: '',
    initialState: null,
    mode: 'client',
  });

  const events = createEvents();

  const plugins = [userIdentify(events)];

  const store = createStore({ plugins });
  const router = createRouter(store, events);
  const device = createDevice();

  sync(store, router);

  const ready = Promise.all([
    errorSetup({ router, context }),
    skeletonKeyStore(store),
    interceptorSetup(http, router, store),
    prefetchSetup({ events, store, router, context }),
    new Promise(r => router.onReady(r)),
  ]);

  // create the app instance.
  const vm = new Vue({
    events,
    router,
    store,
    device,
    render: h => h(App),
  });

  const meta = vm.$meta();

  // eslint-disable-next-line no-param-reassign
  context.meta = meta;

  // note we are not mounting the app here, since bootstrapping will be
  // different depending on whether we are in a browser or on the server.
  return {
    vm,
    ready,
  };
}
